import React, { useState, useEffect } from 'react';
import { X, Brain, Save, RotateCcw, Users } from 'lucide-react';
import { fetchUserSystemPrompt, updateUserSystemPrompt } from '../services/settingsApi';
import { fetchUsers } from '../services/usersApi';

const SystemPrompt = ({ isOpen, onClose, isEmbedded = false, isAdmin = false }) => {
  const [systemPrompt, setSystemPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Admin-specific state
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    if (isOpen || isEmbedded) {
      if (isAdmin) {
        loadUsers();
      } else {
        loadSystemPrompt();
      }
    }
  }, [isOpen, isEmbedded, isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedUserId) {
      loadSystemPrompt(selectedUserId);
    }
  }, [selectedUserId, isAdmin]);

  useEffect(() => {
    setHasChanges(systemPrompt !== originalPrompt);
  }, [systemPrompt, originalPrompt]);

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetchUsers();
      // Backend returns array directly, not { users: [...] }
      const usersList = Array.isArray(response) ? response : (response.users || []);
      setUsers(usersList);
      // Pre-select first user if available
      if (usersList.length > 0) {
        setSelectedUserId(usersList[0].id);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadSystemPrompt = async (userId = null) => {
    setIsLoading(true);
    try {
      const response = await fetchUserSystemPrompt(userId);
      const prompt = response.systemPrompt || '';
      setSystemPrompt(prompt);
      setOriginalPrompt(prompt);
    } catch (error) {
      console.error('Failed to load system prompt:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const userId = isAdmin ? selectedUserId : null;
      await updateUserSystemPrompt(systemPrompt, userId);
      setOriginalPrompt(systemPrompt);
      setHasChanges(false);
      // Success feedback could be added here
    } catch (error) {
      console.error('Failed to save system prompt:', error);
      // Error feedback could be added here
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSystemPrompt(originalPrompt);
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        setSystemPrompt(originalPrompt);
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!isOpen && !isEmbedded) return null;

  const content = (
    <>
      {!isEmbedded && (
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Brain className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Memory</h2>
              <p className="text-sm text-gray-500">Set your personal AI assistant preferences</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      )}

      <div className={`${isEmbedded ? '' : 'flex-1 p-6'} min-h-0`}>
          <div className="space-y-4">
            {/* Admin User Selector */}
            {isAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-blue-600" />
                  <label htmlFor="userSelect" className="text-sm font-medium text-blue-900">
                    Managing Memory For:
                  </label>
                </div>
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center p-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <select
                    id="userSelect"
                    value={selectedUserId || ''}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email} {user.isAdmin ? '(Admin)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div>
              <label htmlFor="systemPrompt" className="block text-sm font-medium text-gray-700 mb-2">
                {isAdmin ? 'User Memory' : 'Personal Memory'}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {isAdmin
                  ? 'Manage memory settings for the selected user. This information will be remembered in all of their conversations.'
                  : 'This information will be remembered in every conversation to personalize how the AI responds to you. For example, you can specify your role, preferred communication style, or any context you\'d like the AI to remember.'}
              </p>
              
              {isLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <textarea
                  id="systemPrompt"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  placeholder="Example: You are helping a software engineer who prefers concise, technical explanations. Always provide code examples when relevant and focus on best practices."
                  className="w-full h-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  maxLength={2000}
                />
              )}
              
              <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                <span>
                  {systemPrompt.length}/2000 characters
                </span>
                {hasChanges && (
                  <span className="text-amber-600">• Unsaved changes</span>
                )}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">💡 Tips for effective memory:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Be specific about your role or profession</li>
                <li>• Mention preferred communication style (formal, casual, technical)</li>
                <li>• Include any recurring context or preferences</li>
                <li>• Keep it concise but descriptive</li>
              </ul>
            </div>
          </div>
        </div>

      {/* Footer */}
      {!isEmbedded && (
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            disabled={!hasChanges || isLoading}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving || isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (isEmbedded) {
    return (
      <div className="space-y-6">
        {content}
        {/* Embedded Save Button */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            onClick={handleReset}
            disabled={!hasChanges || isLoading}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving || isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {content}
      </div>
    </div>
  );
};

export default SystemPrompt;